import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileViewer } from '@intelishs-group/ui';
import { OverlayCarga, OverlayCargaService } from '@intelishs-group/ui/overlay-carga';

/** Debajo de esto, la navegacion se siente instantanea y el velo solo parpadea. */
const UMBRAL_VELO_MS = 180;

/**
 * RAIZ DE COMPOSICION.
 *
 * El outlet, el visor global de archivos y el velo de espera. Lo que necesita
 * sesion vive en `ShellLayout`, que es la ruta PADRE del area autenticada: alli
 * se juntan sesion, catalogo de modulos y shell.
 *
 * ⚠️ Esa separacion no es organizativa, es la correccion de un parpadeo: con el
 * marco detras de un `@if (autenticado())` aqui, la visibilidad del shell la
 * decidia la SESION y su contenido el ROUTER, que cambian en momentos distintos.
 * Ver el docblock de `ShellLayout`.
 */
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, FileViewer, OverlayCarga],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly router = inject(Router);
  private readonly carga = inject(OverlayCargaService);

  constructor() {
    this.vigilarNavegacion();
  }

  /**
   * Enciende el velo mientras el router navega.
   *
   * ⚠️ CON UN UMBRAL, Y NO AL INSTANTE. Casi todas las pantallas van por
   * `loadComponent`, asi que la PRIMERA visita a un modulo baja su chunk y tarda
   * lo suficiente para que el usuario crea que su clic no hizo nada; la segunda
   * ya esta en cache y es inmediata. Mostrando el velo sin umbral, esas
   * navegaciones instantaneas lo encienden y lo apagan en el mismo frame: un
   * destello gris en cada clic del menu, que molesta mas que la espera.
   *
   * Con 180ms: lo lento avisa, lo rapido no parpadea.
   *
   * `NavigationCancel` y `NavigationError` cuentan como fin. Sin ellos, una
   * guarda que redirige —`sesionGuard` mandando al login— cancela la navegacion
   * y el velo se quedaria encendido para siempre, sin ningun error que lo
   * explique.
   */
  private vigilarNavegacion(): void {
    let apagar: (() => void) | null = null;
    let temporizador: ReturnType<typeof setTimeout> | null = null;

    const terminar = (): void => {
      if (temporizador !== null) clearTimeout(temporizador);
      temporizador = null;
      apagar?.();
      apagar = null;
    };

    this.router.events.pipe(takeUntilDestroyed()).subscribe((evento) => {
      if (evento instanceof NavigationStart) {
        // Se reinicia el reloj en cada arranque: dos navegaciones seguidas no
        // deben acumular dos velos.
        terminar();
        temporizador = setTimeout(() => {
          apagar = this.carga.mostrar('Cargando…');
        }, UMBRAL_VELO_MS);
        return;
      }

      if (
        evento instanceof NavigationEnd ||
        evento instanceof NavigationCancel ||
        evento instanceof NavigationError
      ) {
        terminar();
      }
    });
  }
}
